import {
  useKineticMap,
  useStableResultData,
  getNodes,
  MapContext,
  MapSearchResultItem,
  useMapBoxSearchContext,
  DEFAULT_ZOOM,
  useMapSearchBoxPin,
  MapSearchResultProps,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
} from '@kinetic-ui/shared';
import { IntersectionListQuery, useIntersectionListQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import SignalIcon from '@mui/icons-material/Traffic';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';
import { StatusLayerIds } from 'const/statusLayer';

const LAYERS = [
  StatusLayerIds.COMMUNICATION_STATUS_LAYER_ID,
  StatusLayerIds.COORDINATION_STATUS_LAYER_ID,
  StatusLayerIds.MAIN_STREET_GREEN_LAYER_ID,
  StatusLayerIds.PREEMPT_STATUS_LAYER_ID,
  StatusLayerIds.TIME_SYNC_STATUS_LAYER_ID,
];

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();

  const result = useIntersectionListQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });

  const resultData = useStableResultData(result);
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();

  const intersections = useMemo(
    () => getNodes(resultData?.intersections),
    [resultData?.intersections]
  );
  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const getName = useCallback(
    (intersection: IntersectionListQuery['intersections']['edges'][0]['node']) => {
      const intersectionName = intersection.details?.name ?? '';
      const deviceName = intersection.devices?.map((device) => device.name)?.join(' / ');

      if (deviceName) {
        return `${intersectionName} / ${deviceName}`;
      } else {
        return intersectionName;
      }
    },
    []
  );

  const onClick = useCallback(
    (id: string, coordinates: [number, number], openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const intersection = intersections.find((e) => e.id === id);
        let intersectionName = intersection ? getName(intersection) : null;
        if (intersectionName) {
          addSearchHistory({
            name: intersectionName,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Signal,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: coordinates ?? [0, 0] });
        return;
      }
      !selectedLayers.some((layerId) => LAYERS.includes(layerId as StatusLayerIds)) &&
        setSelectedLayers([
          ...selectedLayers,
          StatusLayerIds.MAIN_STREET_GREEN_LAYER_ID,
          StatusLayerIds.ICONS_LAYER_ID,
        ]);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: coordinates[1],
        longitude: coordinates[0],
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      getName,
      hasEntityDrawer,
      intersections,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {intersections.map((intersection) => (
        <MapSearchResultItem
          key={`searchItem_${intersection.id}`}
          name={getName(intersection)}
          onClick={(openInNewWindow) =>
            onClick(intersection.id, intersection.location?.coordinates, openInNewWindow)
          }
          icon={<SignalIcon />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
